import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Plus, Trash2, Check, X, PackageCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { timeAgo, FilterTabs, Badge, STATUS_META } from "./engShared";

const QTABS = [["pending", "Pending"], ["approved", "Approved"], ["fulfilled", "Fulfilled"], ["declined", "Declined"], ["all", "All"]];
const inputCls = "rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-sm w-full";

export default function EngRedemptions() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState({ items: [], counts: {} });
  const [status, setStatus] = useState("pending");
  const [form, setForm] = useState({ title: "", description: "", cost_points: 100, stock: "" });
  const [busy, setBusy] = useState("");

  const loadCatalog = () => api.get("/engagement/redemptions/catalog").then((r) => setItems(r.data.items || []));
  const loadQueue = (s = status) => api.get(`/engagement/redemptions?status=${s}`).then((r) => setQ(r.data));
  useEffect(() => { loadCatalog(); }, []);
  useEffect(() => { loadQueue(status); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const err = (e) => toast.error(e?.response?.data?.detail || "Something went wrong");

  const addItem = async () => {
    try {
      await api.post("/engagement/redemptions/catalog", {
        title: form.title, description: form.description,
        cost_points: Number(form.cost_points) || 0,
        stock: form.stock === "" ? null : Number(form.stock) });
      setForm({ title: "", description: "", cost_points: 100, stock: "" });
      loadCatalog();
    } catch (e) { err(e); }
  };
  const starters = async () => {
    try { await api.post("/engagement/redemptions/catalog/starters"); loadCatalog(); } catch (e) { err(e); }
  };
  const patchItem = async (id, patch) => {
    try { await api.put(`/engagement/redemptions/catalog/${id}`, patch); loadCatalog(); } catch (e) { err(e); }
  };
  const delItem = async (id) => {
    try { await api.delete(`/engagement/redemptions/catalog/${id}`); loadCatalog(); } catch (e) { err(e); }
  };
  const act = async (rid, action) => {
    setBusy(rid);
    try {
      const r = await api.post(`/engagement/redemptions/${rid}/${action}`);
      if (r.data?.emailed) toast.success("Listener notified by email");
      if (r.data?.announced?.length) toast.success(`Shout-out queued for ${r.data.announced.join(", ")}`);
      await loadQueue();
    } catch (e) { err(e); }
    setBusy("");
  };

  return (
    <>
      <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3" data-testid="eng-redemptions-catalog">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-bold"><Gift className="h-4 w-4 text-brand-amber" /> Rewards catalog</div>
          {items && !items.length && (
            <button onClick={starters} data-testid="eng-catalog-starters-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-brand-amber/40 bg-brand-amber/10 text-brand-amber px-3 py-1.5 text-xs font-bold">
              <Sparkles className="h-3.5 w-3.5" /> Add starter items
            </button>
          )}
        </div>
        {(items || []).map((it) => (
          <div key={it.item_id} className="flex items-center gap-3 border-b border-line/50 pb-2 last:border-0" data-testid={`eng-catalog-item-${it.item_id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{it.title}</div>
              {it.description && <div className="text-[11px] text-ink-muted truncate">{it.description}</div>}
            </div>
            <span className="font-mono text-xs text-brand-amber font-bold shrink-0">{it.cost_points} pts</span>
            <span className="font-mono text-[10px] text-ink-muted shrink-0">{it.stock == null ? "∞" : `${it.stock} left`}</span>
            <label className="flex items-center gap-1 text-[10px] text-ink-muted shrink-0">
              <input type="checkbox" checked={!!it.active} data-testid={`eng-catalog-active-${it.item_id}`}
                onChange={(e) => patchItem(it.item_id, { active: e.target.checked })} /> active
            </label>
            <button onClick={() => delItem(it.item_id)} data-testid={`eng-catalog-del-${it.item_id}`}
              className="rounded border border-line p-1 text-rose-300 shrink-0" title="Delete"><Trash2 className="h-3 w-3" /></button>
          </div>
        ))}
        <div className="grid grid-cols-2 sm:grid-cols-[2fr_3fr_1fr_1fr_auto] gap-2 items-end pt-1">
          <div><div className="text-[10px] font-mono uppercase text-ink-muted mb-1">Title</div>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. VIP Studio Tour" data-testid="eng-catalog-new-title" className={inputCls} maxLength={80} /></div>
          <div><div className="text-[10px] font-mono uppercase text-ink-muted mb-1">Description</div>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What the listener gets" data-testid="eng-catalog-new-desc" className={inputCls} maxLength={300} /></div>
          <div><div className="text-[10px] font-mono uppercase text-ink-muted mb-1">Cost pts</div>
            <input type="number" min={1} value={form.cost_points} onChange={(e) => setForm({ ...form, cost_points: e.target.value })} data-testid="eng-catalog-new-cost" className={inputCls} /></div>
          <div><div className="text-[10px] font-mono uppercase text-ink-muted mb-1">Stock</div>
            <input type="number" min={0} value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="∞" data-testid="eng-catalog-new-stock" className={inputCls} /></div>
          <button onClick={addItem} disabled={!form.title.trim()} data-testid="eng-catalog-add-btn"
            className="inline-flex items-center gap-1 rounded-lg bg-brand-amber text-black font-bold text-xs px-3 py-2 disabled:opacity-50"><Plus className="h-3.5 w-3.5" /> Add</button>
        </div>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3" data-testid="eng-redemptions-queue">
        <div className="flex items-center gap-2 text-sm font-bold"><PackageCheck className="h-4 w-4 text-brand-cyan" /> Redemption queue</div>
        <FilterTabs tabs={QTABS} active={status} onChange={setStatus} counts={q.counts} />
        {!q.items?.length && <div className="text-xs text-ink-muted py-2">No {status === "all" ? "" : status + " "}redemptions.</div>}
        {(q.items || []).map((r) => (
          <div key={r.redemption_id} className="flex flex-wrap items-center gap-3 border-b border-line/50 py-2 last:border-0" data-testid={`eng-rdm-row-${r.redemption_id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{r.listener_name || r.listener_email} <span className="text-ink-muted font-normal">wants</span> {r.item_title}</div>
              <div className="text-[10px] text-ink-muted">{r.listener_email} · {timeAgo(r.created_at)}{r.note ? ` · "${r.note}"` : ""}{r.decline_reason ? ` · declined: ${r.decline_reason}` : ""}{r.listener_emailed ? " · emailed ✓" : ""}</div>
            </div>
            <span className="font-mono text-xs text-brand-amber font-bold">-{r.cost_points} pts</span>
            <Badge meta={STATUS_META[r.status]} testid={`eng-rdm-status-${r.redemption_id}`} />
            {r.status === "pending" && (<>
              <button onClick={() => act(r.redemption_id, "approve")} disabled={busy === r.redemption_id} data-testid={`eng-rdm-approve-${r.redemption_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-400/10 text-emerald-300 px-2.5 py-1 text-[10px] font-bold"><Check className="h-3 w-3" /> Approve</button>
              <button onClick={() => act(r.redemption_id, "decline")} disabled={busy === r.redemption_id} data-testid={`eng-rdm-decline-${r.redemption_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-rose-400/40 text-rose-300 px-2.5 py-1 text-[10px] font-bold"><X className="h-3 w-3" /> Decline</button>
            </>)}
            {r.status === "approved" && (<>
              <button onClick={() => act(r.redemption_id, "fulfill")} disabled={busy === r.redemption_id} data-testid={`eng-rdm-fulfill-${r.redemption_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan px-2.5 py-1 text-[10px] font-bold"><PackageCheck className="h-3 w-3" /> Fulfilled</button>
              <button onClick={() => act(r.redemption_id, "decline")} disabled={busy === r.redemption_id} data-testid={`eng-rdm-refund-${r.redemption_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-rose-400/40 text-rose-300 px-2.5 py-1 text-[10px] font-bold"><X className="h-3 w-3" /> Decline &amp; refund</button>
            </>)}
          </div>
        ))}
      </div>
    </>
  );
}
